// Google OAuth 2.0 Frontend Integration
// Implements secure client-side Google Sign-In

/**
 * Initialize Google Sign-In
 * Must be called before using Google authentication
 */
export const initializeGoogleAuth = () => {
  return new Promise((resolve, reject) => {
    // Check if Google Identity Services is already loaded
    if (window.google && window.google.accounts) {
      resolve(window.google);
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.google && window.google.accounts) {
        resolve(window.google);
      } else {
        reject(new Error('Google Identity Services failed to load'));
      }
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load Google Identity Services'));
    };
    
    document.head.appendChild(script);
  });
};

/**
 * Get Google Client ID from environment
 * Frontend only stores public client ID (not secret)
 */
export const getGoogleClientId = () => {
  // Use the actual Google Client ID from environment
  return process.env.REACT_APP_GOOGLE_CLIENT_ID || '658896105005-m5q8tnfmg20ied9fkktsbgfs18pb0q2q.apps.googleusercontent.com';
};

/**
 * Generate nonce for security (prevents replay attacks)
 */
export const generateNonce = async (email, apiUrl) => {
  try {
    const response = await fetch(`${apiUrl}/auth/google-nonce`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate security nonce');
    }

    const data = await response.json();
    return data.nonce;
  } catch (error) {
    console.error('Nonce generation error:', error);
    throw error;
  }
};

/**
 * Handle Google Sign-In response securely
 */
export const handleGoogleSignIn = async (credential, apiUrl) => {
  try {
    // Decode JWT to get email (for nonce generation)
    // Note: We don't verify the token here - that's done server-side
    const payload = JSON.parse(atob(credential.split('.')[1]));
    const email = payload.email;

    if (!email) {
      throw new Error('Email not found in Google token');
    }

    // Generate nonce for security
    const nonce = await generateNonce(email, apiUrl);

    // Send ID token and nonce to backend for verification
    const response = await fetch(`${apiUrl}/auth/google-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        idToken: credential,
        nonce: nonce,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Google login failed');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

/**
 * Initialize Google One Tap sign-in
 */
export const initializeGoogleOneTap = (onSuccess, onError) => {
  const clientId = getGoogleClientId();
  
  if (clientId === 'YOUR_GOOGLE_CLIENT_ID_HERE') {
    console.warn('Google Client ID not configured');
    return;
  }

  if (!window.google || !window.google.accounts) {
    console.error('Google Identity Services not loaded');
    return;
  }

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: onSuccess,
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  // Display One Tap prompt
  window.google.accounts.id.prompt((notification) => {
    if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
      console.log('One Tap not displayed:', notification.getNotDisplayedReason());
    }
  });
};

/**
 * Render Google Sign-In button
 */
export const renderGoogleSignInButton = (elementId, onSuccess, onError) => {
  const clientId = getGoogleClientId();
  
  if (clientId === 'YOUR_GOOGLE_CLIENT_ID_HERE') {
    console.warn('Google Client ID not configured');
    return;
  }

  if (!window.google || !window.google.accounts) {
    console.error('Google Identity Services not loaded');
    return;
  }

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: onSuccess,
  });

  window.google.accounts.id.renderButton(
    document.getElementById(elementId),
    {
      theme: 'outline',
      size: 'large',
      width: '100%',
      text: 'continue_with',
      shape: 'rectangular',
    }
  );
};

/**
 * Sign out from Google
 */
export const signOutFromGoogle = () => {
  if (window.google && window.google.accounts) {
    window.google.accounts.id.disableAutoSelect();
  }
};